import { type IIconProps, Text, Tooltip } from '@ad-lucy/ui';
import { useI18n } from '@i18n/i18n.utils';
import type { StyledDotProps } from '@modules/Layout/Default/Sidebar/Navbar/types';
import type { PropsWithChildren } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useLayoutContext } from '../../../LayoutContext';
import {
  collapseAnimationIcon,
  expandAnimationIcon,
  getListItemMarginTop,
  getRenderSidebarCollapsed,
} from '../../animationCore';
import NavBarLink, { type NavBarLinkProps } from './NavBarLink';
import { getLocalizedNavigationLabel } from './nav.utils';

// Warning: changing the final height of this element directly affects the sidebar animation
// e.g. changing the top or bottom padding/margin values
const StyledListItem = styled.li`
  && {
    display: list-item;
    margin-top: ${({ theme }) => getListItemMarginTop(theme)};
  }
`;

const TooltipHeading = styled(Text)`
  color: ${({ theme }) => theme?.colors?.gray?._500};
`;

type NavigationItemProps = PropsWithChildren<
  {
    nested?: boolean;
    id: string;
    name: string;
    icon?: IIconProps['type'];
    link: string;
    paths: string[];
    dot?: StyledDotProps;
  } & Pick<NavBarLinkProps, 'badge'>
>;

export default function NavBarItem({
  nested,
  children,
  id,
  name,
  link,
  paths,
  dot,
  icon,
  badge,
}: NavigationItemProps) {
  const { t } = useI18n();
  const label = getLocalizedNavigationLabel(t)(name);
  const { pathname } = useLocation();
  const active = paths.some((p) => matchPath(p, pathname));

  // determine collapsed state and matching animation
  const { sidebarCollapsed, sidebarTransitionEnded } = useLayoutContext();
  const renderSidebarCollapsed = getRenderSidebarCollapsed(
    sidebarCollapsed,
    sidebarTransitionEnded,
  );

  // biome-ignore lint/suspicious/noExplicitAny: legacy
  let animation: any;
  if (!(nested || sidebarTransitionEnded)) {
    animation = sidebarCollapsed ? collapseAnimationIcon : expandAnimationIcon;
  }

  // create NavBarLink to
  const routeLink = (
    <NavBarLink
      active={active}
      animation={animation}
      badge={badge}
      collapsed={renderSidebarCollapsed && !nested}
      data-testid={`adm.sidebar.navigation.button.${id}`}
      dot={dot}
      icon={icon}
      text={label}
      to={link}
    />
  );

  return (
    <StyledListItem
      data-active={active}
      data-testid={`adm.sidebar.button.${id}`}
    >
      {!nested && renderSidebarCollapsed ? (
        <Tooltip delayDuration={0}>
          <Tooltip.Trigger asChild={true}>
            {/* anchor div to work as trigger */}
            <div>{routeLink}</div>
          </Tooltip.Trigger>
          <Tooltip.Content side="right" align="start" variant="light">
            <TooltipHeading size="xs">
              {label} {badge?.value ?? ''}
            </TooltipHeading>
            {children}
          </Tooltip.Content>
        </Tooltip>
      ) : (
        <>
          {routeLink}
          {active && children}
        </>
      )}
    </StyledListItem>
  );
}
